import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";

// Contexts
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { PWAProvider, usePWA } from "@/contexts/PWAContext";
import { WeightProvider } from "@/contexts/WeightContext";
import { OfflineDataProvider, useOfflineData } from "@/contexts/OfflineDataContext";
import { ServiceProvider } from "@/contexts/ServiceContext";

// Auth
import { LoginPage, ForgotPasswordPage, ResetPasswordPage, RegisterPage, VerifyEmailPage } from "@/pages/auth";
import { ProtectedRoute, ChangePasswordDialog } from "@/components/auth";
import LandingPage from "@/pages/LandingPage";

// Layout
import { Sidebar, Header } from "@/components/layout";
import OfflineIndicator, { SyncStatus } from "@/components/layout/OfflineIndicator";

// Admin pages
import { AdminDashboard, AdminUpload, AdminUsers, AdminMedications, AdminProtocols, AdminProcedures } from "@/pages/admin";
import AdminErrorReports from "@/pages/admin/AdminErrorReports";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminConcentrations from "@/pages/admin/AdminConcentrations";
import AdminAudit from "@/pages/admin/AdminAudit";

// Protocol Link Context
import { ProtocolLinkContext } from "@/contexts/ProtocolLinkContext";
export { ProtocolLinkContext };

// Main pages
import {
  Dashboard, ProtocolDetail, CategoryPage, FavoritesPage,
  DrugCalculator, MedicationsPage, SymptomsPage, ProceduresPage, ProcedureDetail,
  QuizPage, QuickReferenceCards, CodeTimer, PhoneBook, InterventionTracker, About
} from "@/pages/main";
import ServiceSelector from "@/pages/main/ServiceSelector";
import Onboarding from "@/components/Onboarding";
import UpdateBanner from "@/components/UpdateBanner";
import WhatsNewDialog from "@/components/WhatsNewDialog";

import axios from "axios";
import { Settings, LogIn, LogOut, Key, Smartphone, Menu, X, Moon, Sun, Home, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useRecentHistory } from "@/hooks/useRecentHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==========================================
// AppLayout - Main authenticated layout
// ==========================================
const AppLayout = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { isInstallable, isInstalled, installPWA } = usePWA();
  const { protocols, categories } = useOfflineData();
  const [favorites, setFavorites] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { recentHistory, addToHistory, clearHistory } = useRecentHistory();
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem("ems_onboarding_done"));

  useEffect(() => {
    axios.get(`${API}/favorites`).then(res => setFavorites(res.data)).catch(() => {});
    // Flush any queued offline intervention entries (recovers from prior crashes / network blips)
    import("@/utils/interventionLogger").then(({ drainQueue }) => {
      drainQueue().then(({ sent }) => {
        if (sent > 0) toast.success(`Synced ${sent} queued intervention${sent !== 1 ? "s" : ""}`);
      });
    });
    // Trigger video precache so they're available on cold offline launches
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      axios.get(`${API}/videos`).then((res) => {
        const urls = (res.data?.videos || []).map((v) => v.path);
        navigator.serviceWorker.controller.postMessage({ type: 'PRECACHE_VIDEOS', videos: urls });
      }).catch(() => {
        // Even without the list, ask SW to precache the static known videos
        navigator.serviceWorker.controller.postMessage({ type: 'PRECACHE_VIDEOS', videos: [] });
      });
    }
  }, []);

  const handleToggleFavorite = async (protocolId) => {
    try {
      const existing = favorites.find(f => f.protocol_id === protocolId);
      if (existing) {
        await axios.delete(`${API}/favorites/${protocolId}`);
        setFavorites(favorites.filter(f => f.protocol_id !== protocolId));
        toast.success("Removed from favorites");
      } else {
        const res = await axios.post(`${API}/favorites`, { protocol_id: protocolId });
        setFavorites([...favorites, res.data]);
        toast.success("Added to favorites");
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Error updating favorites");
    }
  };

  return (
      <WeightProvider>
        <div className="min-h-screen bg-background">
          <UpdateBanner />
          {!showOnboarding && <WhatsNewDialog />}
          {showOnboarding && (
            <Onboarding onComplete={() => { setShowOnboarding(false); localStorage.setItem("ems_onboarding_done", "true"); }} />
          )}
          <Header 
            protocols={protocols}
            onNavigate={(path) => navigate(path)}
            user={user}
            onLogout={logout}
            theme={theme}
            onToggleTheme={toggleTheme}
            onMenuClick={() => setSidebarOpen(true)}
          />
        
        <div className="flex">
          {/* Desktop sidebar */}
          <div className="hidden lg:block w-64 shrink-0 border-r border-border h-[calc(100vh-3.5rem)] sticky top-14 overflow-auto">
            <Sidebar categories={categories} onClose={() => {}} recentHistory={recentHistory} clearHistory={clearHistory} />
          </div>
          
          {/* Mobile sidebar sheet */}
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="w-72 p-0" aria-describedby={undefined}>
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <Sidebar categories={categories} onClose={() => setSidebarOpen(false)} recentHistory={recentHistory} clearHistory={clearHistory} />
            </SheetContent>
          </Sheet>
          
          {/* Main content */}
          <main className="flex-1 p-4 md:p-6 lg:p-8 min-w-0">
            <Routes>
              <Route path="/" element={
                localStorage.getItem("yems-service-chosen")
                ? <Dashboard 
                    protocols={protocols} 
                    categories={categories} 
                    favorites={favorites}
                    onToggleFavorite={handleToggleFavorite} 
                  />
                : <Navigate to="/select-service" replace />
              } />
              <Route path="/select-service" element={<ServiceSelector />} />
              <Route path="/protocol/:id" element={
                <ProtocolDetail 
                  favorites={favorites} 
                  onToggleFavorite={handleToggleFavorite}
                  onViewProtocol={addToHistory}
                />
              } />
              <Route path="/category/:categoryId" element={
                <CategoryPage 
                  protocols={protocols}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                />
              } />
              <Route path="/favorites" element={
                <FavoritesPage 
                  protocols={protocols}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                />
              } />
              <Route path="/calculator" element={<DrugCalculator />} />
              <Route path="/medications" element={<MedicationsPage />} />
              <Route path="/symptoms" element={<SymptomsPage />} />
              <Route path="/procedures" element={<ProceduresPage />} />
              <Route path="/procedure/:id" element={<ProcedureDetail />} />
              <Route path="/quiz" element={<QuizPage />} />
              <Route path="/reference-cards" element={
                <QuickReferenceCards protocols={protocols} favorites={favorites} />
              } />
              <Route path="/code-timer" element={<CodeTimer />} />
              <Route path="/phone-book" element={<PhoneBook />} />
              <Route path="/interventions" element={<InterventionTracker />} />
              <Route path="/about" element={<About />} />
              
              {/* Settings */}
              <Route path="/settings" element={
                <div className="max-w-2xl mx-auto space-y-6">
                  <h1 className="font-heading text-2xl font-bold">Settings</h1>
                  <Card>
                    <CardHeader>
                      <CardTitle>Account</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <span className="text-sm text-muted-foreground">Email</span>
                        <p className="font-medium">{user?.email}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Role</span>
                        <p className="font-medium capitalize">{user?.role}</p>
                      </div>
                      <Button variant="outline" onClick={() => setChangePasswordOpen(true)}>
                        <Key className="w-4 h-4 mr-2" />
                        Change Password
                      </Button>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Appearance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" onClick={toggleTheme}>
                        {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                        {theme === "dark" ? "Light Mode" : "Dark Mode"}
                      </Button>
                    </CardContent>
                  </Card>
                  {isInstallable && !isInstalled && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Install App</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Button onClick={installPWA}>
                          <Smartphone className="w-4 h-4 mr-2" />
                          Install for Offline Access
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                  <Card>
                    <CardHeader>
                      <CardTitle>Offline Data</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <SyncStatus />
                    </CardContent>
                  </Card>
                </div>
              } />
              
              {/* Admin routes */}
              {(user?.role === "admin" || user?.role === "editor" || user?.role === "demo_admin") && (
                <>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/upload" element={<AdminUpload />} />
                  {(user?.role === "admin" || user?.role === "demo_admin") && <Route path="/admin/users" element={<AdminUsers />} />}
                  <Route path="/admin/medications" element={<AdminMedications />} />
                  <Route path="/admin/protocols" element={<AdminProtocols />} />
                  <Route path="/admin/procedures" element={<AdminProcedures />} />
                  <Route path="/admin/error-reports" element={<AdminErrorReports />} />
                  <Route path="/admin/videos" element={<AdminVideos />} />
                  <Route path="/admin/concentrations" element={<AdminConcentrations />} />
                  <Route path="/admin/audit" element={<AdminAudit />} />
                </>
              )}
            </Routes>
          </main>
        </div>
        
        <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
        <OfflineIndicator />
      </div>
    </WeightProvider>
  );
};

// ==========================================
// App - Root component
// ==========================================

// One-time cache bust: clears stale SW caches when a new build version is detected
const CACHE_BUST_KEY = "yems-cache-bust-v4";
if (!localStorage.getItem(CACHE_BUST_KEY)) {
  if ('caches' in window) {
    caches.keys().then(names => {
      Promise.all(names.map(name => caches.delete(name))).then(() => {
        localStorage.setItem(CACHE_BUST_KEY, "done");
        // Also tell SW to skip waiting if a new one is pending
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    });
  } else {
    localStorage.setItem(CACHE_BUST_KEY, "done");
  }
  // Clear old IndexedDB to force fresh sync
  try { indexedDB.deleteDatabase('yems-offline'); } catch {}
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PWAProvider>
            <Routes>
              <Route path="/welcome" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/*" element={
                <ProtectedRoute>
                  <OfflineDataProvider>
                    <ServiceProvider>
                      <AppLayout />
                    </ServiceProvider>
                  </OfflineDataProvider>
                </ProtectedRoute>
              } />
            </Routes>
            <Toaster richColors position="top-right" />
          </PWAProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
